// Codes this bar has already applied, per ladder, for the life of the tab. The
// gift engine keeps its own list under a different key so the two never collide.
const CUSTOM_MILESTONE_DISCOUNT_KEY = 'custom_milestone_tier_discounts_v1';

// Tiers already celebrated in this tab, so confetti can tell a reward the
// shopper just earned from one the cart was already carrying. It cannot live on
// the element: a page navigation and a drawer re-render both build a brand new
// bar with no memory of what came before.
const CUSTOM_MILESTONE_CELEBRATED_KEY = 'custom_milestone_celebrated_tiers_v1';

const CUSTOM_MILESTONE_GIFT_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="milestone-gift-icon">' +
  '<path d="M20 13v7a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-7h16zM14.5 2a3.5 3.5 0 0 1 3.163 5.001L21 7a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1l3.337.001a3.5 3.5 0 0 1 5.664-3.95A3.48 3.48 0 0 1 14.5 2zm-5 2a1.5 1.5 0 0 0-.144 2.993L9.5 7H11V5.5a1.5 1.5 0 0 0-1.356-1.493L9.5 4zm5 0l-.144.007a1.5 1.5 0 0 0-1.35 1.349L13 5.5V7h1.5l.144-.007a1.5 1.5 0 0 0 0-2.986L14.5 4z" fill="currentColor"></path>' +
  '</svg>';

class CustomMilestoneBar extends HTMLElement {
  connectedCallback() {
    this.boot();
  }

  disconnectedCallback() {
    this.teardown();
  }

  teardown() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    this.cartUpdateUnsubscriber = null;
    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.resizeObserver = null;
    clearInterval(this.pubsubPoll);
  }

  boot() {
    this.teardown();

    const config = document.getElementById('CustomMilestoneConfig');
    if (!config) return;

    this.tiers = this.readTiers(config);
    if (!this.tiers.length) {
      this.hidden = true;
      return;
    }

    this.showConfetti = config.dataset.showConfetti === 'true';

    this.renderMarkup();

    // Markers line up 1:1 with tiers; the tier objects carry the goal data.
    this.markers = Array.from(this.querySelectorAll('.milestone-marker')).map((marker) => ({ el: marker }));

    this.messageEl = this.querySelector('.milestone-text');
    this.progressTextEl = this.querySelector('.milestone-progress-text');
    this.completedTextEl = this.querySelector('.milestone-completed-text');
    this.progressBarEl = this.querySelector('.milestone-progress-bar');

    this.subscribeToCart();

    // The section renders the current non-gift cart lines so every tier — a
    // bxgy one included — can paint the right state before any cart event.
    this.applyState({
      items: this.readInitialCart(config),
      total_price: Number(config.dataset.cartTotal) || 0,
      discount_codes: this.readInitialCodes(config),
    });

    // Marker positions depend on the final layout, so remeasure the fill once
    // it has settled rather than trusting the first pass.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.fitHeights();
        this.refreshProgress();
      });
    });

    // Everything measures 0 while the drawer is shut, and labels rewrap when it
    // resizes, so refit whenever the bar's own box changes. The fill has to be
    // recomputed here too: a cart update that lands while the drawer is closed
    // is calculated against unmeasurable icons, and opening the drawer is the
    // first moment their real positions can be read.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.fitHeights();
        this.refreshProgress();
      });
      this.resizeObserver.observe(this);
    }
  }

  // Re-derives the fill from the last known cart state against the icons as
  // they are laid out right now.
  refreshProgress() {
    if (!this.progressBarEl || this.hidden || !this.lastStates) return;

    const next = `${this.calculateProgress(this.lastStates)}%`;
    if (this.progressBarEl.style.width !== next) this.progressBarEl.style.width = next;
  }

  // The label and marker rows contain only absolutely-positioned children, so
  // they collapse to nothing on their own and used to rely on a min-height big
  // enough for the worst case — which left a visible gap in every other case.
  // Measuring the tallest child and pinning the row to it removes that gap.
  fitHeights() {
    const fit = (row, childSelector) => {
      if (!row) return;
      const children = [...row.querySelectorAll(childSelector)];
      if (!children.length) return;

      const tallest = Math.max(...children.map((el) => el.getBoundingClientRect().height));
      if (tallest <= 0) return;

      // Only write when it actually differs, so the ResizeObserver settles.
      const next = `${Math.ceil(tallest)}px`;
      if (row.style.height !== next) row.style.height = next;
    };

    fit(this.querySelector('.milestone-amounts'), '.milestone-amount-label');
    fit(this.querySelector('.milestone-markers'), '.milestone-marker');
  }

  readTiers(config) {
    let tiers = [];
    try {
      tiers = JSON.parse(config.dataset.tiers || '[]');
    } catch (error) {
      return [];
    }

    // Editor order is the order along the bar. Mixed goal types (item count vs
    // buy-X) share no unit, so there is nothing sensible to sort by — the
    // merchant's block order is the sequence. Liquid only emits valid tiers.
    return tiers.filter((tier) => tier && tier.reward_name);
  }

  // Cart lines the section rendered, shaped like the ones cart:updated sends.
  readInitialCart(config) {
    try {
      const lines = JSON.parse(config.dataset.cartLines || '[]');
      return Array.isArray(lines) ? lines.map((line) => ({ ...line, properties: {} })) : [];
    } catch (error) {
      return [];
    }
  }

  // Codes on the cart when the page was rendered, shaped like cart.js's
  // discount_codes — lets the first paint notice a code the cart no longer
  // earns. undefined (unknown) when the section predates this attribute.
  readInitialCodes(config) {
    if (config.dataset.discountCodes === undefined) return undefined;
    try {
      const codes = JSON.parse(config.dataset.discountCodes);
      return Array.isArray(codes) ? codes.map((code) => ({ code: String(code) })) : undefined;
    } catch (error) {
      return undefined;
    }
  }

  // Non-gift item count, matching cart-milestone.js.
  countItems(cart) {
    return (cart.items || []).reduce((sum, item) => {
      if (item.properties && item.properties._auto_gift === 'milestone') return sum;
      return sum + item.quantity;
    }, 0);
  }

  // Units of the tier's qualifying product in the cart. Liquid always resolves
  // a real variant id (the engine can only match one), so the product-id branch
  // is just a safety net for a tier that somehow arrives without one.
  countQualifying(cart, tier) {
    return (cart.items || []).reduce((sum, item) => {
      if (item.properties && item.properties._auto_gift === 'milestone') return sum;
      const matches = tier.qualifying_variant_id
        ? item.variant_id === tier.qualifying_variant_id
        : item.product_id === tier.qualifying_product_id;
      return matches ? sum + item.quantity : sum;
    }, 0);
  }

  // Subtotal of non-gift lines, in cents — same reduction cart-milestone.js uses.
  //
  // Measured BEFORE discounts, on purpose. line_price (and its modern name
  // final_line_price) is net of every code already on the cart, so a spend goal
  // that pays out a discount would undo itself: $54.75 earns SAVE10, the 10%
  // comes off, the cart reads $49.28, and the $50 goal it just cleared looks
  // unmet again. original_line_price doesn't move when a code is applied.
  cartSubtotal(cart) {
    return (cart.items || []).reduce((sum, item) => {
      if (item.properties && item.properties._auto_gift === 'milestone') return sum;
      const line = item.original_line_price != null ? item.original_line_price : item.line_price;
      return sum + (line || 0);
    }, 0);
  }

  // ── Free shipping ──────────────────────────────────────────────────────────
  //
  // A Subtotal reward ticked "This reward is free shipping" carries the goal
  // and fees for the shopper's currency (the table moved over from
  // free-shipping-bar.liquid), or {} when that currency isn't in the table.

  freeShipping(tier) {
    const fs = tier && tier.type === 'subtotal' ? tier.free_shipping : null;
    return fs && typeof fs === 'object' ? fs : null;
  }

  // Cart total after every discount, in cents. Free shipping is measured on
  // this, not on cartSubtotal(): it is the total Shopify's shipping rates look
  // at, and the one free-shipping-bar.liquid measured.
  cartTotalAfterDiscounts(cart) {
    return typeof cart.total_price === 'number' ? cart.total_price : this.cartSubtotal(cart);
  }

  // The shipping fee the shopper saves at this total, in cents: the last fee
  // whose "from" the total has reached — same rule as free-shipping-bar.js.
  // null when the currency has no fee in the table.
  shippingFeeCents(fs, totalCents) {
    const fees = Array.isArray(fs.fees) ? [...fs.fees].sort((a, b) => a.from - b.from) : [];
    if (!fees.length) return null;

    const total = totalCents / 100;
    let fee = fees[0].fee;
    fees.forEach((step) => {
      if (total >= step.from) fee = step.fee;
    });
    return Math.round(Number(fee) * 100) || null;
  }

  // Keeps the "Save / Saved" line under the free-shipping marker in step with
  // the cart. The fee can change with the total (some currencies charge less
  // above an amount), so it is worked out again on every update.
  updateFreeShipping(states, cart) {
    const total = this.cartTotalAfterDiscounts(cart);
    let hasFreeShipping = false;

    this.tiers.forEach((tier, index) => {
      const fs = this.freeShipping(tier);
      if (!fs) return;
      hasFreeShipping = true;

      const earned = !!(states[index] && states[index].earned);
      const fee = this.shippingFeeCents(fs, total);

      const line = this.markers[index] && this.markers[index].el.querySelector('.marker-fs-save');
      if (line) line.textContent = fee ? `${earned ? 'Saved' : 'Save'} ${this.formatMoney(fee)}` : '';
    });

    if (!hasFreeShipping) return;

    // "Saved" can wrap onto a second line where "Save" didn't, and the marker
    // row's height is pinned by fitHeights(), so measure it again.
    this.fitHeights();
  }

  // Threshold in cents, resolved per country then through the currency rate,
  // matching cart-milestone.js so the bar and the engine agree.
  // The merchant types this by hand, so bad JSON must degrade to "no overrides"
  // rather than take the tier — or the whole bar — down with it.
  countryThresholds(tier) {
    const raw = tier.country_thresholds;
    if (!raw) return {};
    if (typeof raw === 'object') return raw;

    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  subtotalTargetCents(tier, cart) {
    // A free-shipping reward uses the goal for the shopper's currency as-is. It
    // is already in that currency, so it must not go through the rate.
    const fs = this.freeShipping(tier);
    if (fs && Number(fs.goal) > 0) return Math.round(Number(fs.goal) * 100);

    const country = (cart.shipping_address && cart.shipping_address.country_code) || 'default';
    const perCountry = this.countryThresholds(tier);
    const raw = perCountry[country] !== undefined ? perCountry[country] : tier.threshold;
    const rate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
    return Math.round(Number(raw) * rate * 100);
  }

  // FoxTheme.Currency.formatMoney formats against the shop's default money
  // format, so it keeps printing the shop currency's symbol even when the
  // buyer is shopping in another one — an INR cart rendered as "$1,823.51".
  // Amounts are already in presentment cents, so format against the active
  // presentment currency and only fall back to the theme helper.
  formatMoney(cents) {
    const amount = cents / 100;
    const active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;

    if (active && typeof Intl !== 'undefined' && Intl.NumberFormat) {
      try {
        const locale = document.documentElement.lang || (window.Shopify && window.Shopify.locale) || undefined;
        return new Intl.NumberFormat(locale, { style: 'currency', currency: active }).format(amount);
      } catch (error) {
        // Unknown currency code — drop through to the theme helper.
      }
    }

    if (window.FoxTheme && FoxTheme.Currency && FoxTheme.Currency.formatMoney) {
      return FoxTheme.Currency.formatMoney(cents);
    }

    return amount.toFixed(2);
  }

  // Each tier answers for itself: how far along it is and whether it's earned.
  evaluateTier(tier, cart) {
    if (tier.type === 'subtotal') {
      const need = this.subtotalTargetCents(tier, cart);
      const have = this.freeShipping(tier) ? this.cartTotalAfterDiscounts(cart) : this.cartSubtotal(cart);
      return { type: tier.type, have, need, earned: need > 0 && have >= need };
    }

    if (tier.type === 'bxgy') {
      const need = Number(tier.qualifying_qty) || 1;
      const have = this.countQualifying(cart, tier);
      return { type: tier.type, have, need, earned: have >= need };
    }

    const need = Number(tier.threshold) || 0;
    const have = this.countItems(cart);
    return { type: tier.type, have, need, earned: need > 0 && have >= need };
  }

  // Tiers sit at equal fractions of the track, with the last always at 100 so
  // it is right-anchored in CSS instead of centred (which would overflow).
  tierPositions(count) {
    if (count <= 1) return [100];
    return Array.from({ length: count }, (_, index) => Math.round(((index + 1) / count) * 10000) / 100);
  }

  countLabel(value) {
    return `${value} item${value !== 1 ? 's' : ''}`;
  }

  // The label above a marker when the merchant hasn't set a Goal title.
  defaultGoalLabel(tier) {
    if (tier.type === 'bxgy') return tier.qualifying_title || '';
    // The bare threshold, not the per-country one — this label is rendered
    // once at boot, before any cart with a shipping address exists.
    //
    // Converted with window.Shopify.currency.rate, which is the same market
    // rate Shopify converts product prices with, and the same one it uses to
    // decide whether a discount's minimum is met. Measured on the store: a
    // 4,854.72 cart is refused SAVE5 and a 4,900.00 one is not, which brackets
    // the 4,861.59 this produces. A mid-market rate would print ~4,797 and
    // promise a discount the cart would not get.
    if (tier.type === 'subtotal') return this.formatMoney(this.subtotalTargetCents(tier, {}));
    return this.countLabel(Number(tier.threshold) || 0);
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str == null ? '' : str;
    return div.innerHTML;
  }

  // Marker boxes are a fixed width (--ms-marker-w) and the last one is anchored
  // to the right edge so it can't overflow, which puts its centre half a box in
  // from the end. Spacing every marker across that same reduced span keeps the
  // gaps equal instead of bunching the last two together.
  slotPositionCss(index, count) {
    return `calc((100% - var(--ms-marker-w) / 2) * ${index + 1} / ${count})`;
  }

  renderMarkup() {
    const count = this.tiers.length;
    // Drives --ms-marker-w and the tighter type for crowded three-tier bars.
    this.dataset.count = count;

    const labels = this.tiers
      .map((tier, index) => {
        const atEnd = index === count - 1;
        const text = tier.title ? tier.title : this.defaultGoalLabel(tier);
        return (
          `<span class="milestone-amount-label${atEnd ? ' pos-align-end' : ''}"` +
          `${atEnd ? '' : ` style="left: ${this.slotPositionCss(index, count)};"`}>` +
          `${this.escapeHtml(text)}</span>`
        );
      })
      .join('');

    const markers = this.tiers.map((tier, index) => this.markerMarkup(tier, index, count)).join('');

    this.innerHTML =
      '<div class="custom-milestone-container">' +
      '<div class="milestone-message">' +
      '<span class="milestone-text" data-milestone-state="progress">' +
      '<span class="milestone-progress-text"></span>' +
      '<span class="milestone-completed-text"></span>' +
      '</span>' +
      '</div>' +
      '<div class="milestone-progress-wrapper">' +
      `<div class="milestone-amounts">${labels}</div>` +
      `<div class="milestone-markers" data-count="${this.tiers.length}">` +
      '<div class="milestone-progress-track">' +
      '<div class="milestone-progress-bar no-empty" style="width: 0%;"></div>' +
      '</div>' +
      markers +
      '</div>' +
      '</div>' +
      '</div>';
  }

  markerMarkup(tier, index, count) {
    // The last marker keeps its CSS right-anchoring; the rest are spaced to match.
    const position = index === count - 1 ? '' : ` style="left: ${this.slotPositionCss(index, count)};"`;

    return (
      `<div class="milestone-marker"${position}` +
      ` data-unlock-message="${this.escapeHtml(tier.unlock_message)}"` +
      ` data-achieved-message="${this.escapeHtml(tier.achieved_message)}">` +
      `<div class="marker-icon">${CUSTOM_MILESTONE_GIFT_ICON}</div>` +
      `<span class="marker-title">${this.escapeHtml(tier.reward_name)}</span>` +
      // Filled in by updateFreeShipping(). Kept outside .marker-title so the
      // message still reads the plain reward name.
      (this.freeShipping(tier) ? '<span class="marker-fs-save"></span>' : '') +
      '</div>'
    );
  }

  subscribeToCart() {
    if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.subscribe) {
      this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.cart) this.syncWithCart(event.cart);
      });
      return;
    }

    // This section can boot before theme.js has defined FoxTheme, so wait for it.
    this.pubsubPoll = setInterval(() => {
      if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.subscribe) {
        clearInterval(this.pubsubPoll);
        this.subscribeToCart();
      }
    }, 100);
    setTimeout(() => clearInterval(this.pubsubPoll), 10000);
  }

  // Counts the same non-gift items the gift engine (cart-milestone.js) counts,
  // so the bar and the gift unlock at exactly the same point. Lines the engine
  // added itself carry properties._auto_gift === 'milestone' and are skipped,
  // otherwise earning a gift would push the count towards the next tier.
  syncWithCart(cart) {
    // The only caller is the theme's cartUpdate subscription, so this is the
    // one path where the cart genuinely just changed — and the only one allowed
    // to set confetti off.
    this.applyState(cart, { fromCartEvent: true });
  }

  // Marker icons don't sit at the "clean" fractions tierPositions() assumes —
  // their real position depends on the current CSS layout (flex sizing,
  // padding, label widths). Measuring the rendered box keeps the fill glued to
  // the icons regardless. Both edges are returned, not just the centre: the
  // fill has to stop at a pending icon's leading edge and resume from an
  // earned icon's trailing edge, so it never paints over a locked reward.
  measureMarkerBounds() {
    const track = this.querySelector('.milestone-progress-track');
    if (!track) return null;

    const trackRect = track.getBoundingClientRect();
    if (trackRect.width <= 0) return null;

    const asPct = (x) => ((x - trackRect.left) / trackRect.width) * 100;

    return this.markers.map((milestone) => {
      const icon = milestone.el.querySelector('.marker-icon') || milestone.el;
      const rect = icon.getBoundingClientRect();
      return { leading: asPct(rect.left), center: asPct(rect.left + rect.width / 2), trailing: asPct(rect.right) };
    });
  }

  // How far through its own segment the current tier is, 0-1. Consecutive
  // item-count tiers measure from the previous threshold so 4 items across a
  // 3 → 5 step reads as halfway, not 80%. Anything else (a bxgy tier, or an
  // item-count tier following one) measures from zero, since the two goals
  // share no scale.
  segmentFraction(states, index) {
    const state = states[index];
    if (!state || state.need <= 0) return 0;

    const previous = index > 0 ? states[index - 1] : null;
    const measurable = state.type === 'item_count' || state.type === 'subtotal';
    const sameScale = previous && measurable && previous.type === state.type;
    const floor = sameScale ? previous.need : 0;
    const span = state.need - floor;
    if (span <= 0) return 0;

    return Math.min(1, Math.max(0, (state.have - floor) / span));
  }

  calculateProgress(states) {
    const n = this.markers.length;
    if (!n) return 0;

    const measured = this.measureMarkerBounds();
    // Unmeasurable only while the drawer is shut, where nothing is visible
    // anyway — collapse the bounds onto the nominal position.
    const fallback = this.tierPositions(n).map((pos) => ({ leading: pos, center: pos, trailing: pos }));
    const boundsAt = (index) => (measured ? measured[index] : fallback[index]);

    // The fill runs to the first tier still outstanding, not to a count of
    // earned ones — with mixed goal types a later tier can be earned while an
    // earlier one isn't, and filling past the gap would misrepresent progress.
    const pending = states.findIndex((state) => !state.earned);

    // Nothing outstanding: fill the whole track edge-to-edge for a clean
    // "fully done" look, rather than stopping at the last marker's position.
    if (pending === -1) return 100;

    // Start past the previous (earned) icon, stop at the pending icon's near
    // edge — the bar only crosses a gift once that gift is actually unlocked.
    const lowPct = pending === 0 ? 0 : boundsAt(pending - 1).trailing;
    const highPct = boundsAt(pending).leading;
    const span = Math.max(0, highPct - lowPct);
    const ratio = this.segmentFraction(states, pending);
    const pct = Math.min(100, Math.max(0, lowPct + ratio * span));

    // Rounded so the inline width stays a short string rather than 33.33333…%.
    return Math.round(pct * 100) / 100;
  }

  // ── Discount-only rewards ──────────────────────────────────────────────────
  //
  // A reward can be a discount instead of a gift. Those tiers produce no
  // campaign for the gift engine (cart-milestone.js), which only ever applies a
  // code alongside an auto-gift line it added — so the code would never reach
  // the cart. The bar already evaluates every tier, so it manages them here.
  //
  // Tiers that DO have a gift product are deliberately skipped: the engine owns
  // those, and applying the same code from both places would double up.
  //
  // "Manages" means both directions. The bar used to only ever ADD a code and
  // relied on the code's minimum purchase in Admin to drop it again. Without
  // that minimum, a cart that earned SAVE5 at $69 and then fell back to $67.20
  // kept the 5% off. Now each block keeps exactly one of its own codes on the
  // cart — the one for the highest tier reached — and removes the others. The
  // shopper's own codes are never touched.

  readAppliedDiscounts() {
    let stored = {};
    try {
      const parsed = JSON.parse(sessionStorage.getItem(CUSTOM_MILESTONE_DISCOUNT_KEY) || '{}');
      if (parsed && typeof parsed === 'object') stored = parsed;
    } catch (error) {
      // Unreadable storage — the in-memory record below still answers.
    }

    // Private mode and a full quota both turn sessionStorage into a black hole:
    // the write silently does nothing and every read comes back empty. A bar
    // that can't see its own write would re-apply on the refresh the code
    // triggers, and loop. The in-memory copy is the source of truth for this
    // page — kept on window, so it outlives the element when the bar is rebuilt
    // (an editor reload, a re-boot) — and storage only carries it across page
    // loads.
    return Object.assign(stored, window.CustomMilestoneAppliedDiscounts || {});
  }

  // { ladder: code } for a code the bar put on the cart; { ladder: null } once
  // that block's goal is lost, so crossing the goal again applies it again.
  writeAppliedDiscounts(changes) {
    const current = this.readAppliedDiscounts();
    const ladders = Object.keys(changes).filter((ladder) => (current[ladder] || null) !== changes[ladder]);
    if (!ladders.length) return;

    const memory = window.CustomMilestoneAppliedDiscounts || (window.CustomMilestoneAppliedDiscounts = {});
    ladders.forEach((ladder) => {
      memory[ladder] = changes[ladder];
      current[ladder] = changes[ladder];
    });

    try {
      sessionStorage.setItem(CUSTOM_MILESTONE_DISCOUNT_KEY, JSON.stringify(current));
    } catch (error) {
      // Private mode or a full quota — the in-memory record covers this page.
    }
  }

  // Which of this bar's codes the cart should carry, worked out from one cart.
  // Returns the full list to send (the shopper's own codes kept as they are),
  // whether it differs from what the cart has, and the record to keep.
  discountPlan(cart) {
    const states = this.tiers.map((tier) => this.evaluateTier(tier, cart));
    const known = Array.isArray(cart.discount_codes);
    const current = known ? cart.discount_codes.map((entry) => entry && entry.code).filter(Boolean) : [];
    // Shopify matches codes regardless of case, so this does too.
    const has = (code) => current.some((onCart) => onCart.toLowerCase() === code.toLowerCase());
    const applied = this.readAppliedDiscounts();

    // Every tier in one block shares a goal type, so the largest earned
    // requirement is the highest tier the shopper has reached. Picking it stops
    // a 20% reward being undercut by the 5% one it supersedes.
    const ladders = new Map();
    this.tiers.forEach((tier, index) => {
      const code = (tier.discount_code || '').trim();
      if (!code || tier.gift_variant_id) return;

      const ladder = tier.ladder || tier.id;
      const entry = ladders.get(ladder) || { codes: [], best: null };
      entry.codes.push(code);

      const state = states[index];
      if (state && state.earned && (!entry.best || state.need >= entry.best.need)) {
        entry.best = { code, need: state.need };
      }
      ladders.set(ladder, entry);
    });

    const remove = new Set();
    const add = [];
    const record = {};
    let drift = false;

    ladders.forEach((entry, ladder) => {
      const best = entry.best ? entry.best.code : null;

      // Every one of this block's codes that isn't the one earned right now.
      entry.codes.forEach((code) => {
        if (best && code.toLowerCase() === best.toLowerCase()) return;
        if (has(code)) remove.add(code.toLowerCase());
      });

      // A cart that arrives without its code list can't show a stale code, but
      // the record can: the bar applied something this cart no longer earns.
      if (!known && applied[ladder] && applied[ladder] !== best) drift = true;

      if (!best) {
        record[ladder] = null;
      } else if (has(best)) {
        record[ladder] = best;
      } else if (applied[ladder] !== best) {
        add.push(best);
        record[ladder] = best;
      }
      // Otherwise the bar applied this code before and it's gone now: the
      // shopper removed it, so it stays off rather than fighting them.
    });

    return {
      known,
      changed: remove.size > 0 || add.length > 0 || drift,
      codes: current.filter((code) => !remove.has(code.toLowerCase())).concat(add),
      record,
    };
  }

  // Looks at the cart the bar was just handed, and only if its codes look
  // wrong passes the decision to syncDiscounts().
  checkDiscounts(cart, fromCartEvent) {
    const plan = this.discountPlan(cart);
    if (plan.changed) {
      // A genuine cart event carries the cart exactly as Shopify just returned
      // it, so the sync can act on it straight away. A repaint's cart may be
      // stale, so the sync fetches its own.
      this.syncDiscounts(fromCartEvent ? cart : null);
      return;
    }

    // Nothing to send. A genuine cart event is fresh enough to keep the record
    // current; a repaint's cart may be stale, so it leaves the record alone.
    if (fromCartEvent && plan.known) this.writeAppliedDiscounts(plan.record);
  }

  // Puts the right codes on the cart, in as few round trips as possible — each
  // one is time the shopper spends looking at a discount the cart no longer
  // earns. It used to take three after the theme's own quantity request
  // (re-fetch /cart.js, /cart/update, then a cart:refresh that fetched the
  // drawer again): a few seconds on the store. Now it is one.
  //
  // 1. freshCart — the cart from a genuine cart event IS fresh: it is the
  //    theme's own /cart/change.js reply. Only a repaint (the page-load cart in
  //    the config section, stale after any change) or a re-run fetches /cart.js.
  //    Acting on a stale cart could add a code the cart no longer earns, or
  //    remove one it does, and loop.
  // 2. One /cart/update request sets the codes AND returns the drawer's new
  //    HTML (Section Rendering API), and that reply is published exactly the
  //    way the theme publishes its own — no separate redraw.
  //
  // One sync at a time for the whole page. A request that arrives meanwhile
  // runs once more at the end, on a freshly fetched cart, instead of being
  // dropped.
  syncDiscounts(eventCart) {
    const sync =
      window.CustomMilestoneDiscountSync ||
      (window.CustomMilestoneDiscountSync = { running: false, again: false, redraw: false });
    if (sync.running) {
      sync.again = true;
      return;
    }
    sync.running = true;

    const routes = (window.FoxTheme && FoxTheme.routes) || {};
    const cartUrl = routes.cart_url || '/cart';
    const updateUrl = routes.cart_update_url || '/cart/update';

    const freshCart =
      eventCart && Array.isArray(eventCart.discount_codes)
        ? Promise.resolve(eventCart)
        : fetch(`${cartUrl}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(
            (response) => response.json()
          );

    freshCart
      .then((cart) => {
        // Without the cart's own code list, the list sent below would wipe the
        // shopper's codes — so do nothing rather than guess.
        if (!cart || !Array.isArray(cart.discount_codes) || this.countItems(cart) === 0) return null;

        const plan = this.discountPlan(cart);
        if (!plan.changed) {
          this.writeAppliedDiscounts(plan.record);
          return null;
        }

        // The drawer (and the cart page, if this is it) register their section
        // ids on this event — the same one the theme's coupon form and quantity
        // buttons use to bundle the HTML into their request.
        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
        );

        // The theme's own coupon form sets codes the same way (cart.js,
        // CartDiscount / CartDiscountRemove): /cart/update with the full
        // comma-separated list, so one request adds the new code and removes
        // the old one together.
        const body = new FormData();
        body.append('discount', plan.codes.join(','));
        if (sections.length) {
          body.append('sections', sections.join(','));
          body.append('sections_url', window.location.pathname);
        }

        return fetch(`${updateUrl}.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        }).then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          this.writeAppliedDiscounts(plan.record);
          return response.json();
        });
      })
      .then((updated) => {
        if (!updated) return;

        // Another cart action landed while this was in flight, so this reply
        // may be older than what the drawer shows now. Drawing it could put an
        // old quantity back on screen — leave it to the re-run below.
        if (sync.again) {
          sync.redraw = true;
          return;
        }

        sync.redraw = false;
        this.publishCart(updated);
      })
      .catch((error) => console.warn('[CustomMilestone] Could not update the discount codes', error))
      .then(() => {
        sync.running = false;
        if (sync.again) {
          sync.again = false;
          this.syncDiscounts(null);
          return;
        }
        // The last reply was skipped and the re-run had nothing to send: the
        // drawer still needs drawing from the cart as it is now.
        if (sync.redraw) {
          sync.redraw = false;
          this.refreshCart();
        }
      });
  }

  // Hands the /cart/update reply to the theme exactly as its own cart actions
  // do — cartUpdate with the cart and the drawer HTML — so the drawer redraws
  // from it (CartItems.onCartUpdate) with no further request. Without the HTML
  // (no drawer on this page) the theme's refresh is the fallback.
  publishCart(cart) {
    const pubsub = window.FoxTheme && FoxTheme.pubsub;
    if (pubsub && pubsub.publish && cart && Array.isArray(cart.items) && cart.sections) {
      pubsub.publish(pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
      return;
    }
    this.refreshCart();
  }

  // The drawer is rendered from the cart as it stood BEFORE the codes changed,
  // and nothing redraws it afterwards — so the totals would keep reading the
  // old price until some later cart action happened to re-render.
  //
  // cart:refresh is the theme's own hook (cart.js, CartDrawer.onCartRefresh):
  // it re-fetches the drawer section plus /cart.js and republishes cartUpdate.
  // open:false so a code earned on a collection page doesn't throw the drawer
  // open. Re-entrant by design — the republished cartUpdate runs applyState
  // again, but the fresh cart now matches the plan, so nothing is sent and the
  // cycle stops.
  refreshCart() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
  }

  // ── Confetti ───────────────────────────────────────────────────────────────
  //
  // Two separate things used to fire it on pages where nothing had been earned.
  //
  // 1. The trigger read the marker's own class — a marker without
  //    .current-reward counted as a new unlock. renderMarkup() rebuilds those
  //    markers from scratch on every boot, so a cart that qualified an hour ago
  //    looked new on every page load. Dropping a tier fired it too, because the
  //    lower marker had not been current either.
  //
  // 2. boot() replays config.dataset.cartLines, which Liquid renders once with
  //    the page and never updates. The drawer re-rendering (the cart:refresh
  //    that applying a discount triggers, among others) rebuilds the bar and
  //    feeds it that stale cart, so the bar briefly believes tiers were lost
  //    and then regained them when the real cart event landed.
  //
  // Hence both rules below: confetti is only ever considered on a genuine cart
  // event, and the boot paint may seed the record but must never overwrite it.

  // Tier identity has to outlive the element, so it comes from the config, not
  // the DOM. Liquid stamps "<block id>-<index>" on every tier; the fallback only
  // covers a hand-edited config that somehow arrives without one.
  tierKey(tier, index) {
    return String(tier.id || `${index}:${tier.type}:${tier.threshold}`);
  }

  // { ready } says whether this tab has a record at all — without one there is
  // no way to tell "just earned" from "already earned", so the caller stays quiet.
  readCelebrated() {
    // The window copy is written alongside storage so it is never staler, and it
    // is what keeps this working in private mode, where sessionStorage silently
    // swallows writes. It dies on navigation; storage carries across.
    if (Array.isArray(window.CustomMilestoneCelebrated)) {
      return { ready: true, keys: window.CustomMilestoneCelebrated };
    }

    try {
      const parsed = JSON.parse(sessionStorage.getItem(CUSTOM_MILESTONE_CELEBRATED_KEY));
      if (Array.isArray(parsed)) return { ready: true, keys: parsed };
    } catch (error) {
      // Unreadable storage — fall through and treat this as no record.
    }

    return { ready: false, keys: [] };
  }

  writeCelebrated(keys) {
    window.CustomMilestoneCelebrated = keys;
    try {
      sessionStorage.setItem(CUSTOM_MILESTONE_CELEBRATED_KEY, JSON.stringify(keys));
    } catch (error) {
      // Private mode or a full quota — the window copy covers this page.
    }
  }

  // The tiers that crossed from unearned to earned since the last cart event.
  recordEarnedTiers(states, fromCartEvent) {
    const earned = this.tiers
      .map((tier, index) => (states[index] && states[index].earned ? this.tierKey(tier, index) : null))
      .filter(Boolean);

    const previous = this.readCelebrated();

    if (!fromCartEvent) {
      // Boot paint. Its cart comes from the config's server-rendered lines,
      // which are stale the moment the cart changes, so it may only establish
      // a record that does not exist yet — never replace a live one.
      if (!previous.ready) this.writeCelebrated(earned);
      return [];
    }

    this.writeCelebrated(earned);

    // Nothing to compare against: this cart event is the baseline, not a win.
    if (!previous.ready) return [];
    return earned.filter((key) => !previous.keys.includes(key));
  }

  applyState(cart, options) {
    // Only a cart event means the shopper just did something. Everything else —
    // boot, a re-boot from shopify:section:load, the drawer rebuilding itself —
    // is a repaint of a cart that was already there.
    const fromCartEvent = !!(options && options.fromCartEvent);

    this.lastCart = cart;
    const itemCount = this.countItems(cart);
    this.hidden = itemCount === 0;
    if (itemCount === 0) {
      // An empty cart earns nothing, so record that. Emptying and rebuilding is
      // a real unlock rather than a repeat.
      this.recordEarnedTiers([], fromCartEvent);
      return;
    }

    const states = this.tiers.map((tier) => this.evaluateTier(tier, cart));
    this.lastStates = states;

    // Tiers are NOT necessarily earned in order once goal types are mixed — a
    // buy-X tier can be satisfied while an earlier item-count tier is not. So
    // each marker reflects its own state, and "current" is the furthest-along
    // earned tier rather than a count.
    let currentIndex = -1;
    states.forEach((state, index) => {
      if (state.earned) currentIndex = index;
    });

    this.markers.forEach((milestone, index) => {
      const achieved = !!(states[index] && states[index].earned);
      const isCurrent = index === currentIndex;
      const wasCurrent = milestone.el.classList.contains('current-reward');

      milestone.el.classList.toggle('achieved', achieved);
      milestone.el.classList.toggle('current-reward', isCurrent);

      if (isCurrent && !wasCurrent) {
        milestone.el.classList.add('milestone-pop');
        setTimeout(() => milestone.el.classList.remove('milestone-pop'), 500);
      }
    });

    this.updateMessage(states);
    this.updateFreeShipping(states, cart);
    if (this.progressBarEl) this.progressBarEl.style.width = `${this.calculateProgress(states)}%`;

    this.checkDiscounts(cart, fromCartEvent);

    const newlyEarned = this.recordEarnedTiers(states, fromCartEvent);
    if (fromCartEvent && newlyEarned.length && this.showConfetti) this.playConfetti();
  }

  renderTemplate(template, vars) {
    let html = template;
    if (vars.goal_diff !== undefined) {
      html = html
        .split('{{goal_diff}}')
        .join(`<span class="highlight milestone-amount">${this.escapeHtml(vars.goal_diff)}</span>`);
    }
    if (vars.reward !== undefined) {
      html = html
        .split('{{reward}}')
        .join(`<span class="highlight milestone-reward-name">${this.escapeHtml(vars.reward)}</span>`);
    }
    return html;
  }

  // What the shopper still has to do for the next tier, phrased per goal type.
  goalGapText(tier, state) {
    if (tier.type === 'subtotal') {
      return this.formatMoney(Math.max(0, state.need - state.have));
    }

    if (tier.type === 'bxgy') {
      const remaining = Math.max(0, state.need - state.have);
      const name = tier.qualifying_title || 'the qualifying product';
      return state.need > 1 ? `${remaining} × ${name}` : name;
    }

    return this.countLabel(Math.max(0, state.need - state.have));
  }

  updateMessage(states) {
    if (!this.messageEl) return;

    // The message speaks to the first tier still outstanding; only when none
    // are outstanding is the bar complete.
    const pending = states.findIndex((state) => !state.earned);
    const completed = pending === -1;
    this.messageEl.dataset.milestoneState = completed ? 'completed' : 'progress';
    this.dataset.milestoneState = completed ? 'completed' : 'progress';

    if (completed) {
      const current = this.markers[this.markers.length - 1];
      if (this.completedTextEl && current) {
        const title = current.el.querySelector('.marker-title');
        const rewardName = title ? title.textContent : '';
        const template = current.el.dataset.achievedMessage || "You've unlocked {{reward}}";
        this.completedTextEl.innerHTML = this.renderTemplate(template, { reward: rewardName });
      }
      return;
    }

    const next = this.markers[pending];
    const nextTier = this.tiers[pending];
    if (!next || !nextTier) return;

    const goalDiff = this.goalGapText(nextTier, states[pending]);
    const title = next.el.querySelector('.marker-title');
    const rewardName = title ? title.textContent : '';
    const defaultTemplates = {
      bxgy: 'Buy {{goal_diff}} to unlock {{reward}}',
      subtotal: 'Spend {{goal_diff}} more to unlock {{reward}}',
      item_count: 'Add {{goal_diff}} to unlock {{reward}}',
    };
    const defaultTemplate = defaultTemplates[nextTier.type] || defaultTemplates.item_count;
    const template = next.el.dataset.unlockMessage || defaultTemplate;

    if (this.progressTextEl) {
      this.progressTextEl.innerHTML = this.renderTemplate(template, { goal_diff: goalDiff, reward: rewardName });
    }
  }

  playConfetti() {
    if (window.confetti) {
      window.confetti({ particleCount: 100, spread: 80, origin: { x: 0.9, y: 0.5 }, zIndex: 1999 });
      return;
    }
    if (window.CustomMilestoneConfettiLoading) return;
    window.CustomMilestoneConfettiLoading = true;

    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.6.0/dist/confetti.browser.min.js';
    script.onload = () => {
      if (window.confetti) window.confetti({ particleCount: 100, spread: 80, origin: { x: 0.9, y: 0.5 }, zIndex: 1999 });
    };
    document.head.appendChild(script);
  }
}

if (!customElements.get('custom-milestone-bar')) {
  customElements.define('custom-milestone-bar', CustomMilestoneBar);
}

// Editing the config section re-renders it with new settings; the bar lives in
// a different section, so rebuild it from the fresh config.
document.addEventListener('shopify:section:load', (event) => {
  if (!event.target.querySelector('#CustomMilestoneConfig')) return;
  const bar = document.getElementById('CustomMilestoneBarContainer');
  if (bar && typeof bar.boot === 'function') bar.boot();
});
